import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { subscriptionService } from '../../_services';
import { toast } from 'react-toastify';

export default function SubscriptionPlanManager() {
  const [subscriptions, setSubscriptions] = useState([]);

  const [requesting, setRequesting] = useState(false)

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    subscriptionId: '',
    name: '',
    description: '',
    price: 0,
    billing: '',
    badge: '',
    period: '',
    periodValue: '',
    periodUnit: 'M',
    gracePeriod: '',
    gracePeriodValue: '',
    gracePeriodUnit: 'M',
    transitionDisabled: false,
    isDev: false,
    isActive: true
  });

  const resetForm = () => {
    setFormData({
      subscriptionId: '',
      name: '',
      description: '',
      price: 0,
      billing: '',
      badge: '',
      period: '',
      periodValue: '',
      periodUnit: 'M',
      gracePeriod: '',
      gracePeriodValue: '',
      gracePeriodUnit: 'M',
      transitionDisabled: false,
      isDev: false,
      isActive: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  useEffect(() => {
    setRequesting(true)
    const fetchPlans = async () => {
      try {
        const res = await subscriptionService.getSubscriptionPlans()
        if(res.status) {
          const subscription = res.data
          setSubscriptions(subscription)
        } else {
          console.log(res.message);
          toast.error("Something went wrong!!");
        }
      } catch (error) {
        console.log(error);
        toast.error("Something went wrong!!");
      }
      setRequesting(false);
    };
    fetchPlans();
  }, []);

  const parsePeriod = (periodStr) => {
    const match = periodStr.match(/^(\d+)([YMdhm])$/);
    if (match) {
      return { value: match[1], unit: match[2] };
    }
    return { value: '', unit: 'M' };
  };

  const handleEdit = (subscription) => {
    const periodParsed = parsePeriod(subscription.period);
    const gracePeriodParsed = parsePeriod(subscription.gracePeriod);
    setFormData({
      subscriptionId: subscription.subscriptionId,
      name: subscription.name,
      description: subscription.description,
      price: subscription.price,
      billing: subscription.billing,
      badge: subscription.badge,
      period: subscription.period,
      periodValue: periodParsed.value,
      periodUnit: periodParsed.unit,
      gracePeriod: subscription.gracePeriod,
      gracePeriodValue: gracePeriodParsed.value,
      gracePeriodUnit: gracePeriodParsed.unit,
      transitionDisabled: subscription.transitionDisabled || false,
      isDev: subscription.isDev || false,
      isActive: subscription.isActive
    });
    setEditingId(subscription.subscriptionId);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this subscription?')) {
      setSubscriptions(subscriptions.filter(sub => sub._id !== id));
    }
  };

  const handleSubmit = async () => {
    if (!formData.subscriptionId || !formData.name || !formData.period || !formData.gracePeriod || !formData.price || formData.price == '') {
      alert('Please fill in all required fields');
      return;
    }
    
    if
     (editingId) {
      const newFormData = {
        ...formData,
        period: `${formData.periodValue}${formData.periodUnit}`,
        periodValue: undefined,
        periodUnit: undefined,
        gracePeriod: `${formData.gracePeriodValue}${formData.gracePeriodUnit}`,
        gracePeriodValue: undefined,
        gracePeriodUnit: undefined,
      }
      const res = await subscriptionService.updateSubscriptionPlan(newFormData)
      if(res.status) {
        setSubscriptions(subscriptions.map(sub => 
          sub.subscriptionId === editingId 
            ? { 
                ...sub,
                ...res.data
              }
            : sub
        ));
        toast.success(res.message)
      } else {
        toast.error("Something went wrong")
      }
    } else {
      const newSubscription = {
        _id: Math.random().toString(36).substr(2, 9),
        ...formData,
        savings: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setSubscriptions([...subscriptions, newSubscription]);
    }
    
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value)
    });
  };

  return (
    <div className="tw-min-h-screen tw-bg-gray-50 tw-p-6">
      <div className="tw-max-w-7xl tw-mx-auto">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          {/* <button
            onClick={() => setShowForm(true)}
            className="tw-flex tw-items-center tw-gap-2 tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg hover:tw-bg-blue-700 tw-transition"
          >
            <Plus size={20} />
            Add Subscription
          </button> */}
        </div>
  
        {showForm && (
          <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-p-4 tw-z-50">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-screen tw-overflow-y-auto">
              <div className="tw-flex tw-justify-between tw-items-center tw-p-6 tw-border-b">
                <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900">
                  {editingId ? 'Edit Subscription' : 'New Subscription'}
                </h2>
                <button onClick={resetForm} className="tw-text-gray-500 hover:tw-text-gray-700">
                  <X size={24} />
                </button>
              </div>
              
              <div className="tw-p-6 tw-space-y-4">
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                      Subscription ID *
                    </label>
                    <input
                      readOnly={true}
                      type="text"
                      name="subscriptionId"
                      value={formData.subscriptionId}
                      onChange={handleChange}
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-bg-gray-200 tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                  </div>
                </div>
  
                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="2"
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                  />
                </div>
  
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                      Price *
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                      Billing
                    </label>
                    <input
                      type="text"
                      name="billing"
                      value={formData.billing}
                      onChange={handleChange}
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                  </div>
                </div>
  
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                      Period *
                    </label>
                    <input
                      type="number"
                      name="periodValue"
                      value={formData.periodValue}
                      onChange={handleChange}
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                    <select
                      name="periodUnit"
                      value={formData.periodUnit}
                      onChange={handleChange}
                      className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-200"
                      required
                    >
                      <option value={null}>Select Unit</option>
                      <option value={'m'}>minute(-s)</option>
                      <option value={'h'}>hour(-s)</option>
                      <option value={'d'}>day(-s)</option>
                      <option value={'M'}>Month(-s)</option>
                      <option value={'Y'}>Year(-s)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                      Grace Period *
                    </label>
                    <input
                      type="number"
                      name="gracePeriodValue"
                      value={formData.gracePeriodValue}
                      onChange={handleChange}
                      className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                    />
                    <select
                      name="gracePeriodUnit"
                      value={formData.gracePeriodUnit}
                      onChange={handleChange}
                      className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-200 tw-rounded-xl focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-200"
                      required
                    >
                      <option value={null}>Select Unit</option>
                      <option value={'m'}>minute(-s)</option>
                      <option value={'h'}>hour(-s)</option>
                      <option value={'d'}>day(-s)</option>
                      <option value={'M'}>Month(-s)</option>
                      <option value={'Y'}>Year(-s)</option>
                    </select>
                  </div>
                </div>
  
                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                    Badge
                  </label>
                  <input
                    type="text"
                    name="badge"
                    value={formData.badge}
                    onChange={handleChange}
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
                  />
                </div>
  
                <div className="tw-flex tw-gap-6">
                  <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                    <input
                      type="checkbox"
                      name="transitionDisabled"
                      checked={formData.transitionDisabled}
                      onChange={handleChange}
                      className="tw-w-4 tw-h-4 tw-text-blue-600 tw-rounded focus:tw-ring-2 focus:tw-ring-blue-500"
                    />
                    <span className="tw-text-sm tw-font-medium tw-text-gray-700">Transition Disabled</span>
                  </label>
                  
                  <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                    <input
                      type="checkbox"
                      name="isDev"
                      checked={formData.isDev}
                      onChange={handleChange}
                      className="tw-w-4 tw-h-4 tw-text-blue-600 tw-rounded focus:tw-ring-2 focus:tw-ring-blue-500"
                    />
                    <span className="tw-text-sm tw-font-medium tw-text-gray-700">Is Dev</span>
                  </label>
                  
                  <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="tw-w-4 tw-h-4 tw-text-blue-600 tw-rounded focus:tw-ring-2 focus:tw-ring-blue-500"
                    />
                    <span className="tw-text-sm tw-font-medium tw-text-gray-700">Active</span>
                  </label>
                </div>
  
                <div className="tw-flex tw-gap-3 tw-pt-4">
                  <button
                    onClick={handleSubmit}
                    className="btn btn-primary"
                  >
                    {editingId ? 'Update' : 'Create'} Subscription
                  </button>
                  <button
                    onClick={resetForm}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
  
        <div className="tw-bg-white tw-rounded-lg tw-shadow tw-overflow-hidden">
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full">
              <thead className="tw-bg-gray-100 tw-border-b">
                <tr>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-700 tw-uppercase tw-tracking-wider">Name</th>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-700 tw-uppercase tw-tracking-wider">ID</th>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-700 tw-uppercase tw-tracking-wider">Price</th>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-700 tw-uppercase tw-tracking-wider">Period</th>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-700 tw-uppercase tw-tracking-wider">Badge</th>
                  <th className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-700 tw-uppercase tw-tracking-wider">Status</th>
                  <th className="tw-px-6 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-700 tw-uppercase tw-tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-gray-200">
                {subscriptions && subscriptions.map((sub) => (
                  <tr key={sub._id} className="hover:tw-bg-gray-50">
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                      <div className="tw-font-medium tw-text-gray-900">{sub.name}</div>
                      <div className="tw-text-sm tw-text-gray-500">{sub.description}</div>
                    </td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-sm tw-text-gray-700">
                      {sub.subscriptionId}
                    </td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                      <div className="tw-text-sm tw-font-medium tw-text-gray-900">${sub.price}</div>
                      <div className="tw-text-xs tw-text-gray-500">{sub.billing || '-'}</div>
                    </td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                      <div className="tw-text-sm tw-text-gray-900">{sub.period}</div>
                      <div className="tw-text-xs tw-text-gray-500">Grace: {sub.gracePeriod}</div>
                    </td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                      {sub.badge && (
                        <span className="tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-purple-100 tw-text-purple-800">
                          {sub.badge}
                        </span>
                      )}
                      {sub.isDev && (
                        <span className="tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-yellow-100 tw-text-yellow-800 tw-ml-1">
                          DEV
                        </span>
                      )}
                    </td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap">
                      <span className={`tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full ${
                        sub.isActive ? 'tw-bg-green-100 tw-text-green-800' : 'tw-bg-red-100 tw-text-red-800'
                      }`}>
                        {sub.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="tw-px-6 tw-py-4 tw-whitespace-nowrap tw-text-right tw-text-sm tw-font-medium">
                      <button
                        onClick={() => handleEdit(sub)}
                        className="tw-text-blue-600 hover:tw-text-blue-900 tw-mr-3 tw-border-0 tw-bg-transparent"
                      >
                        Edit
                      </button>
                      {/* <button
                        onClick={() => handleDelete(sub._id)}
                        className="tw-text-red-600 hover:tw-text-red-900"
                      >
                        <Trash2 size={18} />
                      </button> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
  
}